import { useCallback, useContext, useEffect, useRef } from "react";
import { useDebounce, useOnClickOutside } from "usehooks-ts";
import contextBabylon from "../../../../../context/index.js";
import { getSuggestions } from "../services/index.js";
import { useStore } from "../store/reducer.js";
import { collectionByGroup } from "../utils/index.js";
import { DEBOUNCE_DELAY, MIN_ITEMS_TO_SHOW } from "../utils/autocomplete.js";
import useKeydown from "./useKeydown.js";
const useAutocomplete = (refSuggestions, { endpoint: { url , queryParams  } = {} , value , onChange: propsOnChange , forceGetDataOnRender  })=>{
    const usestore = useStore((state)=>({
            ...state,
            selectedSuggestion: value,
            value: value?.text ?? '',
            query: value?.text ?? ''
        }));
    const { store , closeSuggestions , setSuggestions , cancelSuggestion , setValue , setQuery , setPreviousValue , openSuggestions , confirmSuggestion  } = usestore;
    useOnClickOutside(refSuggestions, cancelSuggestion);
    useKeydown(usestore, propsOnChange);
    const context = useContext(contextBabylon);
    const language = context?.LanguageValue?.toLocaleLowerCase() ?? 'es';
    const onClick = (refInput)=>{
        if (refInput?.current?.value) refInput.current.value = store.previousValue ?? '';
    };
    const onBlurHandler = ()=>{
        if (store.value.length > 0 && store.value.length < MIN_ITEMS_TO_SHOW && store.selectedSuggestion?.text) {
            setValue(store.selectedSuggestion?.text);
        } else if (store.value === '') {
            setValue(store.previousValue);
            setPreviousValue('');
        }
    };
    const onChange = useCallback((newValue)=>{
        confirmSuggestion(newValue);
        if (propsOnChange) {
            propsOnChange(newValue);
        }
    }, [
        confirmSuggestion,
        propsOnChange
    ]);
    const forcedGetDataOnRender = useRef(false);
    const debouncedQuery = useDebounce(store.query, DEBOUNCE_DELAY);
    const setPreviousValueHandler = useCallback(()=>{
        if (debouncedQuery?.length > 0 && (debouncedQuery != store.selectedSuggestion?.text || forceGetDataOnRender && !forcedGetDataOnRender.current)) setPreviousValue(debouncedQuery);
    }, [
        debouncedQuery,
        forceGetDataOnRender,
        setPreviousValue,
        store.selectedSuggestion?.text
    ]);
    const getData = useCallback(()=>{
        setPreviousValueHandler();
        if (store.suggestions.length) return;
        if (debouncedQuery.length >= MIN_ITEMS_TO_SHOW && debouncedQuery != store.selectedSuggestion?.text || forceGetDataOnRender && !forcedGetDataOnRender.current) {
            getSuggestions({
                query: debouncedQuery,
                language,
                ...queryParams
            }, context.BaseUrl, url).then((data)=>{
                const suggestionByType = collectionByGroup(data.d);
                setSuggestions(suggestionByType);
            }).catch((err)=>{
                if (store.suggestions.length > 0) {
                    setSuggestions([]);
                }
                console.error('[Autocomplete] ', err);
            });
        } else {
            if (store.suggestions.length > 0) {
                setSuggestions([]);
            }
            if (store.showSuggestions) {
                closeSuggestions();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        debouncedQuery,
        language,
        url,
        context.BaseUrl,
        setSuggestions,
        closeSuggestions
    ]);
    const searchSuggestion = useCallback(({ target: { value  }  })=>{
        if (value) {
            setValue(value);
            setQuery(value);
            openSuggestions();
        } else {
            setPreviousValue(store.selectedSuggestion?.text ?? '');
            if (store.value) setValue('');
            if (store.query) setQuery('');
        }
    }, [
        openSuggestions,
        setQuery,
        setValue,
        store.query,
        store.value,
        setPreviousValue,
        store.selectedSuggestion?.text
    ]);
    const isNewSuggestion = useCallback((autocompleteResult)=>value?.id !== autocompleteResult?.id, [
        value
    ]);
    // Update the internal state using the value prop
    useEffect(()=>{
        if (value) confirmSuggestion(value);
        if (!value) confirmSuggestion(undefined);
    }, [
        confirmSuggestion,
        value
    ]);
    useEffect(()=>{
        getData();
    }, [
        getData
    ]);
    return {
        ...usestore,
        searchSuggestion,
        isNewSuggestion,
        onChange,
        openSuggestions,
        onClick,
        onBlurHandler
    };
};
export default useAutocomplete;

//# sourceMappingURL=useAutocomplete.js.map